import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, Subscription, fromEvent } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import {
  Notification,
  NotificationDetail,
  ConnectionState,
  NotificationReceivedEvent,
  NotificationReadEvent,
  AllNotificationsReadEvent,
  NotificationArchivedEvent,
  CounterUpdatedEvent,
  BatchOperationEvent,
  NotificationSettings,
  NotificationUpdatedEvent,
} from '../models/notification.model';

const SIGNALR_EVENTS = {
  NotificationReceived: 'NotificationReceived',
  NotificationRead: 'NotificationRead',
  AllNotificationsRead: 'AllNotificationsRead',
  NotificationArchived: 'NotificationArchived',
  CounterUpdated: 'CounterUpdated',
  BatchOperation: 'BatchOperation',
  NotificationUpdated: 'NotificationUpdated',
} as const;

const DEFAULT_SETTINGS: NotificationSettings = {
  soundEnabled: true,
  soundVolume: 0.5,
  toastEnabled: true,
  toastDuration: 5000,
};

const SETTINGS_STORAGE_KEY = 'notification_settings';
const MAX_RECONNECT_ATTEMPTS = 5;
const POLLING_INTERVAL_MS = 30000;
const BROADCAST_CHANNEL_NAME = 'cafler_notifications';

interface BroadcastMessage {
  type: 'notification_received' | 'notification_read' | 'all_read' | 'counter_updated' | 'archived' | 'settings_changed' | 'notification_updated' | 'postponement_changed';
  payload: unknown;
  timestamp: number;
  tabId: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationWebSocketService implements OnDestroy {
  private connection: signalR.HubConnection | null = null;
  private destroy$ = new Subject<void>();
  private pollingSubscription?: Subscription;
  private broadcastChannel: BroadcastChannel | null = null;
  private tabId = crypto.randomUUID();
  private audioContext: AudioContext | null = null;

  private connectionStateSubject = new BehaviorSubject<ConnectionState>('disconnected');
  public connectionState$ = this.connectionStateSubject.asObservable();

  private reconnectAttemptSubject = new BehaviorSubject<number>(0);
  public reconnectAttempt$ = this.reconnectAttemptSubject.asObservable();

  private notificationReceivedSubject = new Subject<Notification>();
  public notificationReceived$ = this.notificationReceivedSubject.asObservable();

  private notificationReadSubject = new Subject<NotificationReadEvent>();
  public notificationRead$ = this.notificationReadSubject.asObservable();

  private allNotificationsReadSubject = new Subject<AllNotificationsReadEvent>();
  public allNotificationsRead$ = this.allNotificationsReadSubject.asObservable();

  private notificationArchivedSubject = new Subject<NotificationArchivedEvent>();
  public notificationArchived$ = this.notificationArchivedSubject.asObservable();

  private counterUpdatedSubject = new Subject<CounterUpdatedEvent>();
  public counterUpdated$ = this.counterUpdatedSubject.asObservable();

  private batchOperationSubject = new Subject<BatchOperationEvent>();
  public batchOperation$ = this.batchOperationSubject.asObservable();

  private notificationUpdatedSubject = new Subject<NotificationUpdatedEvent>();
  public notificationUpdated$ = this.notificationUpdatedSubject.asObservable();

  private postponementChangedSubject = new Subject<void>();
  public postponementChanged$ = this.postponementChangedSubject.asObservable();

  private settingsSubject = new BehaviorSubject<NotificationSettings>(this.loadSettings());
  public settings$ = this.settingsSubject.asObservable();

  private isPollingActive = false;
  private hubUrl: string;

  constructor(
    private authService: AuthService,
    private ngZone: NgZone
  ) {
    this.hubUrl = this.buildHubUrl();
    this.initBroadcastChannel();
    this.setupVisibilityHandler();
  }

  private buildHubUrl(): string {
    const baseUrl = environment.cafler_backoffice_back_url.replace('/api', '');
    return `${baseUrl}/notifications-hub`;
  }

  private initBroadcastChannel(): void {
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
        if (event.data.tabId !== this.tabId) {
          this.handleBroadcastMessage(event.data);
        }
      };
    }
  }

  private handleBroadcastMessage(message: BroadcastMessage): void {
    this.ngZone.run(() => {
      switch (message.type) {
        case 'notification_received':
          this.notificationReceivedSubject.next(message.payload as Notification);
          break;
        case 'notification_read':
          this.notificationReadSubject.next(message.payload as NotificationReadEvent);
          break;
        case 'all_read':
          this.allNotificationsReadSubject.next(message.payload as AllNotificationsReadEvent);
          break;
        case 'counter_updated':
          this.counterUpdatedSubject.next(message.payload as CounterUpdatedEvent);
          break;
        case 'archived':
          this.notificationArchivedSubject.next(message.payload as NotificationArchivedEvent);
          break;
        case 'notification_updated':
          this.notificationUpdatedSubject.next(message.payload as NotificationUpdatedEvent);
          break;
        case 'settings_changed':
          this.settingsSubject.next(message.payload as NotificationSettings);
          break;
        case 'postponement_changed':
          this.postponementChangedSubject.next();
          break;
      }
    });
  }

  private broadcast(type: BroadcastMessage['type'], payload: unknown): void {
    if (this.broadcastChannel) {
      const message: BroadcastMessage = {
        type,
        payload,
        timestamp: Date.now(),
        tabId: this.tabId,
      };
      this.broadcastChannel.postMessage(message);
    }
  }

  private setupVisibilityHandler(): void {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (document.visibilityState === 'visible' && this.connectionStateSubject.value === 'disconnected') {
            this.reconnect();
          }
        });
    }
  }

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    this.connectionStateSubject.next('connecting');

    try {
      const token = await this.authService.getAccessToken();
      if (!token) {
        console.warn('[NotificationWebSocket] No token available, cannot connect');
        this.connectionStateSubject.next('disconnected');
        this.startPollingFallback();
        return;
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: async () => {
            const token = await this.authService.getAccessToken();
            if (!token) {
              console.warn('[NotificationWebSocket] Token unavailable during reconnection, disconnecting');
              this.disconnect();
              return '';
            }
            return token;
          },
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount >= MAX_RECONNECT_ATTEMPTS) {
              return null;
            }
            const delays = [0, 2000, 5000, 10000, 30000];
            return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
          },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.setupConnectionHandlers();
      this.setupEventHandlers();

      await this.connection.start();
      this.connectionStateSubject.next('connected');
      this.reconnectAttemptSubject.next(0);
      this.stopPollingFallback();
    } catch (error) {
      console.error('[NotificationWebSocket] Connection failed:', error);
      this.connectionStateSubject.next('failed');
      this.startPollingFallback();
    }
  }

  private setupConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      console.warn('[NotificationWebSocket] Reconnecting...', error);
      this.ngZone.run(() => {
        this.connectionStateSubject.next('reconnecting');
        const currentAttempt = this.reconnectAttemptSubject.value + 1;
        this.reconnectAttemptSubject.next(currentAttempt);
      });
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[NotificationWebSocket] Reconnected:', connectionId);
      this.ngZone.run(() => {
        this.connectionStateSubject.next('connected');
        this.reconnectAttemptSubject.next(0);
        this.stopPollingFallback();
      });
    });

    this.connection.onclose((error) => {
      console.warn('[NotificationWebSocket] Connection closed:', error);
      this.ngZone.run(() => {
        this.connectionStateSubject.next('disconnected');
        if (this.reconnectAttemptSubject.value >= MAX_RECONNECT_ATTEMPTS) {
          this.connectionStateSubject.next('failed');
          this.startPollingFallback();
        }
      });
    });
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.on(SIGNALR_EVENTS.NotificationReceived, (event: NotificationReceivedEvent) => {
      this.ngZone.run(() => {
        this.notificationReceivedSubject.next(event.notification);
        this.broadcast('notification_received', event.notification);
        this.playNotificationSound();
      });
    });

    this.connection.on(SIGNALR_EVENTS.NotificationRead, (event: NotificationReadEvent) => {
      this.ngZone.run(() => {
        this.notificationReadSubject.next(event);
        this.broadcast('notification_read', event);
      });
    });

    this.connection.on(SIGNALR_EVENTS.AllNotificationsRead, (event: AllNotificationsReadEvent) => {
      this.ngZone.run(() => {
        this.allNotificationsReadSubject.next(event);
        this.broadcast('all_read', event);
      });
    });

    this.connection.on(SIGNALR_EVENTS.NotificationArchived, (event: NotificationArchivedEvent) => {
      this.ngZone.run(() => {
        this.notificationArchivedSubject.next(event);
        this.broadcast('archived', event);
      });
    });

    this.connection.on(SIGNALR_EVENTS.CounterUpdated, (event: CounterUpdatedEvent) => {
      this.ngZone.run(() => {
        this.counterUpdatedSubject.next(event);
        this.broadcast('counter_updated', event);
      });
    });

    this.connection.on(SIGNALR_EVENTS.BatchOperation, (event: BatchOperationEvent) => {
      this.ngZone.run(() => {
        this.batchOperationSubject.next(event);
      });
    });

    this.connection.on(SIGNALR_EVENTS.NotificationUpdated, (event: NotificationUpdatedEvent) => {
      this.ngZone.run(() => {
        this.notificationUpdatedSubject.next(event);
        this.broadcast('notification_updated', event);
      });
    });
  }

  async disconnect(): Promise<void> {
    this.stopPollingFallback();
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[NotificationWebSocket] Error disconnecting:', error);
      }
      this.connection = null;
    }
    this.connectionStateSubject.next('disconnected');
  }

  private async reconnect(): Promise<void> {
    const currentAttempt = this.reconnectAttemptSubject.value;
    if (currentAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[NotificationWebSocket] Max reconnect attempts reached');
      this.connectionStateSubject.next('failed');
      this.startPollingFallback();
      return;
    }

    this.reconnectAttemptSubject.next(currentAttempt + 1);
    await this.connect();
  }

  async markAsRead(notificationId: string): Promise<NotificationDetail | null> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      try {
        return await this.connection.invoke<NotificationDetail>('MarkAsRead', notificationId);
      } catch (error) {
        console.error('[NotificationWebSocket] Error marking as read:', error);
        return null;
      }
    }
    return null;
  }

  async getUnreadCount(): Promise<number> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      try {
        return await this.connection.invoke<number>('GetUnreadCount');
      } catch (error) {
        console.error('[NotificationWebSocket] Error getting unread count:', error);
        return 0;
      }
    }
    return 0;
  }

  private startPollingFallback(): void {
    if (this.isPollingActive) return;

    this.isPollingActive = true;
    this.pollingSubscription = timer(0, POLLING_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private stopPollingFallback(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
    this.isPollingActive = false;
  }

  private playNotificationSound(): void {
    const settings = this.settingsSubject.value;
    if (!settings.soundEnabled) return;

    try {
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gainNode.gain.value = settings.soundVolume * 0.3;

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('[NotificationWebSocket] Could not play sound:', error);
    }
  }

  broadcastPostponementChanged(): void {
    this.broadcast('postponement_changed', null);
  }

  updateSettings(settings: Partial<NotificationSettings>): void {
    const newSettings = { ...this.settingsSubject.value, ...settings };
    this.settingsSubject.next(newSettings);
    this.saveSettings(newSettings);
    this.broadcast('settings_changed', newSettings);
  }

  private loadSettings(): NotificationSettings {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('[NotificationWebSocket] Could not load settings:', error);
    }
    return DEFAULT_SETTINGS;
  }

  private saveSettings(settings: NotificationSettings): void {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('[NotificationWebSocket] Could not save settings:', error);
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): ConnectionState {
    return this.connectionStateSubject.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
}
